import { getSiteConfig } from '../siteConfig';
import { AUTOPILOTEE_BRAND } from './autopilotee';
import { LENVOY_BRAND } from './lenvoy';
import { SERVICES_BRAND } from './services';
import { STAYS_BRAND } from './stays';
import { BrandConfig, BrandId, PolicyDocument } from './types';

export * from './types';
export { AUTOPILOTEE_BRAND, LENVOY_BRAND, SERVICES_BRAND, STAYS_BRAND };

export const BRANDS: Record<BrandId, BrandConfig> = {
  autopilotee: AUTOPILOTEE_BRAND,
  lenvoy: LENVOY_BRAND,
  stays: STAYS_BRAND,
  services: SERVICES_BRAND,
};

export function getBrandById(id: BrandId | undefined | null): BrandConfig {
  return (id && BRANDS[id]) || AUTOPILOTEE_BRAND;
}

/** The brand of this page load: from the site config of the current host. */
export function getBrand(): BrandConfig {
  return getBrandById(getSiteConfig().brandId);
}

/** Public path of a policy markdown file for the given brand (default: this page's brand). */
export function brandPolicyPath(doc: PolicyDocument, brand: BrandConfig = getBrand()): string {
  return `${brand.policyBasePath}/${doc}.md`;
}

/** Route and default title of each policy page. */
export const POLICY_PAGES: Record<PolicyDocument, { route: string; title: string }> = {
  terms: { route: '/policies/terms', title: 'Terms of Service' },
  cancellation: { route: '/policies/cancellation', title: 'Cancellation Policy' },
  privacy: { route: '/policies/privacy', title: 'Privacy Policy' },
  'account-deletion': { route: '/account-deletion', title: 'Account Deletion' },
  'renter-agreement': { route: '/policies/renter-agreement', title: 'Rental Agreement' },
  'host-agreement': { route: '/policies/host-agreement', title: 'Host Agreement' },
};

/**
 * Every brand publishes these. A brand without its own Host Agreement serves the default
 * one in `public/policies` (generated from Lenvoy's, see generate-brand-policies.mjs).
 */
const COMMON_POLICIES: PolicyDocument[] = ['terms', 'cancellation', 'privacy', 'account-deletion'];

/** The policies this brand publishes, in navigation order. */
export function brandPolicies(brand: BrandConfig = getBrand()): PolicyDocument[] {
  return [...COMMON_POLICIES, ...(brand.extraPolicies ?? []), 'host-agreement'];
}

export function brandHasPolicy(doc: PolicyDocument, brand: BrandConfig = getBrand()): boolean {
  return brandPolicies(brand).includes(doc);
}

export function brandPolicyTitle(doc: PolicyDocument, brand: BrandConfig = getBrand()): string {
  return brand.policyTitles?.[doc] ?? POLICY_PAGES[doc].title;
}
